import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { orderService } from '../services/orderService';
import Button from '../components/Button/Button';
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner';

const OrderConfirmationWrapper = styled.div`
  min-height: 100vh;
  padding: ${props => props.theme.spacing.xxl} 0;
  background-color: ${props => props.theme.colors.background.secondary};
  padding-top: 90px; /* Account for fixed header */
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.lg};
`;

const SuccessIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: ${props => props.theme.colors.success};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${props => props.theme.spacing.xl};
  color: white;
  font-size: ${props => props.theme.fontSize.xxl};
`;

const OrderConfirmationCard = styled.div`
  background-color: ${props => props.theme.colors.background.primary};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xxl};
  box-shadow: ${props => props.theme.shadows.sm};
  text-align: center;
`;

const ConfirmationTitle = styled.h1`
  font-size: ${props => props.theme.fontSize.xxl};
  font-weight: ${props => props.theme.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const ConfirmationSubtitle = styled.p`
  font-size: ${props => props.theme.fontSize.base};
  color: ${props => props.theme.colors.text.secondary};
  margin-bottom: ${props => props.theme.spacing.xxl};
  line-height: 1.5;
`;

const OrderDetails = styled.div`
  background-color: ${props => props.theme.colors.background.secondary};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.xl};
  margin-bottom: ${props => props.theme.spacing.xxl};
  text-align: left;
`;

const OrderDetailsTitle = styled.h2`
  font-size: ${props => props.theme.fontSize.lg};
  font-weight: ${props => props.theme.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const OrderInfo = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.xl};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const InfoGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const InfoLabel = styled.span`
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  font-weight: ${props => props.theme.fontWeight.medium};
`;

const InfoValue = styled.span`
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.text.primary};
  font-weight: ${props => props.theme.fontWeight.medium};
`;

const OrderStatus = styled.span`
  display: inline-block;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.fontSize.xs};
  font-weight: ${props => props.theme.fontWeight.medium};
  background-color: ${props => {
    switch (props.status) {
      case 'completed':
        return props.theme.colors.success + '15';
      case 'processing':
        return props.theme.colors.info + '15';
      case 'pending':
        return props.theme.colors.warning + '15';
      case 'cancelled':
        return props.theme.colors.error + '15';
      default:
        return props.theme.colors.background.tertiary;
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'completed':
        return props.theme.colors.success;
      case 'processing':
        return props.theme.colors.info;
      case 'pending':
        return props.theme.colors.warning;
      case 'cancelled':
        return props.theme.colors.error;
      default:
        return props.theme.colors.text.secondary;
    }
  }};
`;

const OrderItems = styled.div`
  border-top: 1px solid ${props => props.theme.colors.border.light};
  padding-top: ${props => props.theme.spacing.lg};
`;

const OrderItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${props => props.theme.spacing.md} 0;
  border-bottom: 1px solid ${props => props.theme.colors.border.light};

  &:last-child {
    border-bottom: none;
  }
`;

const ItemInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`;

const ItemName = styled.span`
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.medium};
  color: ${props => props.theme.colors.text.primary};
`;

const ItemQuantity = styled.span`
  font-size: ${props => props.theme.fontSize.xs};
  color: ${props => props.theme.colors.text.secondary};
`;

const ItemPrice = styled.span`
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.medium};
  color: ${props => props.theme.colors.primary};
`;

const OrderTotal = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${props => props.theme.spacing.lg};
  padding-top: ${props => props.theme.spacing.lg};
  border-top: 2px solid ${props => props.theme.colors.border.medium};
`;

const TotalLabel = styled.span`
  font-size: ${props => props.theme.fontSize.base};
  font-weight: ${props => props.theme.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
`;

const TotalValue = styled.span`
  font-size: ${props => props.theme.fontSize.lg};
  font-weight: ${props => props.theme.fontWeight.bold};
  color: ${props => props.theme.colors.primary};
`;

// Downloads Section Styles
const DownloadsSection = styled.div`
  background-color: ${props => props.theme.colors.success}08;
  border: 1px solid ${props => props.theme.colors.success}20;
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.xl};
  margin-bottom: ${props => props.theme.spacing.xxl};
  text-align: left;
`;

const DownloadsSectionTitle = styled.h3`
  font-size: ${props => props.theme.fontSize.lg};
  font-weight: ${props => props.theme.fontWeight.semibold};
  color: ${props => props.theme.colors.success};
  margin-bottom: ${props => props.theme.spacing.lg};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};

  &::before {
    content: '⬇️';
    font-size: ${props => props.theme.fontSize.xl};
  }
`;

const DownloadsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const DownloadItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${props => props.theme.spacing.md};
  background-color: ${props => props.theme.colors.background.primary};
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.border.light};
`;

const DownloadInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
  flex: 1;
`;

const DownloadName = styled.span`
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.medium};
  color: ${props => props.theme.colors.text.primary};
`;

const DownloadMeta = styled.span`
  font-size: ${props => props.theme.fontSize.xs};
  color: ${props => props.theme.colors.text.secondary};
`;

const DownloadButton = styled(Button)`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.fontSize.xs};
`;

const DownloadNote = styled.p`
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.success};
  margin-top: ${props => props.theme.spacing.md};
  font-style: italic;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  justify-content: center;
  flex-wrap: wrap;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
  }
`;

const NextSteps = styled.div`
  background-color: ${props => props.theme.colors.info}08;
  border: 1px solid ${props => props.theme.colors.info}20;
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.xxl};
  text-align: left;
`;

const NextStepsTitle = styled.h3`
  font-size: ${props => props.theme.fontSize.base};
  font-weight: ${props => props.theme.fontWeight.semibold};
  color: ${props => props.theme.colors.info};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const NextStepsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const NextStepsItem = styled.li`
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: ${props => props.theme.spacing.sm};
  padding-left: ${props => props.theme.spacing.lg};
  position: relative;

  &::before {
    content: '✓';
    position: absolute;
    left: 0;
    color: ${props => props.theme.colors.success};
    font-weight: ${props => props.theme.fontWeight.bold};
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const ErrorMessage = styled.div`
  background-color: ${props => props.theme.colors.error}08;
  border: 1px solid ${props => props.theme.colors.error}20;
  color: ${props => props.theme.colors.error};
  padding: ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.md};
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const LoadingDownloads = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing.lg};
  color: ${props => props.theme.colors.text.secondary};
  font-size: ${props => props.theme.fontSize.sm};
  gap: ${props => props.theme.spacing.sm};
`;

const OrderConfirmationPage = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadsLoading, setDownloadsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasDownloadableProducts, setHasDownloadableProducts] = useState(false);

  useEffect(() => {
    const fetchOrderData = async () => {
      try {
        setLoading(true);
        const orderData = await orderService.getOrderById(orderId);
        setOrder(orderData);

        // Check if order has downloadable products
        const hasDownloads = await orderService.hasDownloadableProducts(orderId);
        setHasDownloadableProducts(hasDownloads);

        // If order is completed and has downloadable products, fetch downloads
        if (orderData.status === 'completed' && hasDownloads) {
          setDownloadsLoading(true);
          try {
            const orderDownloads = await orderService.getOrderDownloads(orderId);
            setDownloads(orderDownloads);
          } catch (downloadError) {
            console.warn('Error fetching downloads:', downloadError);
          } finally {
            setDownloadsLoading(false);
          }
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (orderId) {
      fetchOrderData();
    }
  }, [orderId]);

  const formatPrice = (price) => `₹${parseFloat(price).toFixed(2)}`;

  const handleDownload = (downloadUrl, fileName) => {
    // Create a temporary link element and trigger download
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = fileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return <LoadingSpinner fullHeight />;
  }

  if (error || !order) {
    return (
      <OrderConfirmationWrapper>
        <Container>
          <OrderConfirmationCard>
            <ErrorMessage>
              {error || 'Order not found'}
            </ErrorMessage>
            <ActionButtons>
              <Button as={Link} to="/dashboard">
                Go to Dashboard
              </Button>
              <Button as={Link} to="/shop" variant="outline">
                Continue Shopping
              </Button>
            </ActionButtons>
          </OrderConfirmationCard>
        </Container>
      </OrderConfirmationWrapper>
    );
  }

  return (
    <OrderConfirmationWrapper>
      <Container>
        <OrderConfirmationCard>
          <SuccessIcon>✓</SuccessIcon>
          
          <ConfirmationTitle>Order Confirmed!</ConfirmationTitle>
          <ConfirmationSubtitle>
            Thank you for your purchase! Your order has been successfully placed 
            and is being processed. You will receive an email confirmation shortly.
          </ConfirmationSubtitle>

          <OrderDetails>
            <OrderDetailsTitle>Order Details</OrderDetailsTitle>
            
            <OrderInfo>
              <InfoGroup>
                <InfoLabel>Order Number</InfoLabel>
                <InfoValue>#{order.number}</InfoValue>
              </InfoGroup>

              <InfoGroup>
                <InfoLabel>Order Date</InfoLabel>
                <InfoValue>{orderService.formatOrderDate(order.date_created)}</InfoValue>
              </InfoGroup>

              <InfoGroup>
                <InfoLabel>Status</InfoLabel>
                <InfoValue>
                  <OrderStatus status={order.status}>
                    {orderService.formatOrderStatus(order.status)}
                  </OrderStatus>
                </InfoValue>
              </InfoGroup>

              <InfoGroup>
                <InfoLabel>Payment Method</InfoLabel>
                <InfoValue>{order.payment_method_title}</InfoValue>
              </InfoGroup>

              <InfoGroup>
                <InfoLabel>Billing Email</InfoLabel>
                <InfoValue>{order.billing.email}</InfoValue>
              </InfoGroup>

              <InfoGroup>
                <InfoLabel>Total Amount</InfoLabel>
                <InfoValue>{formatPrice(order.total)}</InfoValue>
              </InfoGroup>
            </OrderInfo>

            <OrderItems>
              {order.line_items.map((item, index) => (
                <OrderItem key={index}>
                  <ItemInfo>
                    <ItemName>{item.name}</ItemName>
                    <ItemQuantity>Quantity: {item.quantity}</ItemQuantity>
                  </ItemInfo>
                  <ItemPrice>{formatPrice(item.total)}</ItemPrice>
                </OrderItem>
              ))}
            </OrderItems>

            <OrderTotal>
              <TotalLabel>Total</TotalLabel>
              <TotalValue>{formatPrice(order.total)}</TotalValue>
            </OrderTotal>
          </OrderDetails>

          {/* Downloads Section */}
          {hasDownloadableProducts && (
            <DownloadsSection>
              <DownloadsSectionTitle>
                Your Downloads
              </DownloadsSectionTitle>
              
              {order.status === 'completed' ? (
                <>
                  {downloadsLoading ? (
                    <LoadingDownloads>
                      <LoadingSpinner size="16px" showText={false} />
                      Loading your downloads...
                    </LoadingDownloads>
                  ) : downloads.length > 0 ? (
                    <>
                      <DownloadsList>
                        {downloads.map((download, index) => (
                          <DownloadItem key={index}>
                            <DownloadInfo>
                              <DownloadName>{download.name}</DownloadName>
                              <DownloadMeta>
                                From: {download.product_name}
                                {download.downloads_remaining && 
                                  ` • ${download.downloads_remaining} downloads remaining`
                                }
                              </DownloadMeta>
                            </DownloadInfo>
                            <DownloadButton
                              size="sm"
                              onClick={() => handleDownload(
                                orderService.getSecureDownloadUrl(
                                  download.file, 
                                  download.order_id, 
                                  download.product_id
                                ),
                                orderService.formatDownloadFileName(download.name)
                              )}
                            >
                              Download
                            </DownloadButton>
                          </DownloadItem>
                        ))}
                      </DownloadsList>
                      <DownloadNote>
                        Your download links are also available in your dashboard.
                      </DownloadNote>
                    </>
                  ) : (
                    <p>Downloads will be available once your payment is processed.</p>
                  )}
                </>
              ) : (
                <p>
                  Your digital downloads will be available once your order is completed and payment is confirmed.
                </p>
              )}
            </DownloadsSection>
          )}

          <NextSteps>
            <NextStepsTitle>What happens next?</NextStepsTitle>
            <NextStepsList>
              <NextStepsItem>
                You will receive an email confirmation with your order details
              </NextStepsItem>
              {hasDownloadableProducts && (
                <NextStepsItem>
                  Download links will be sent to your email once payment is confirmed
                </NextStepsItem>
              )}
              <NextStepsItem>
                You can track your order status in your dashboard
              </NextStepsItem>
              <NextStepsItem>
                Our team will process your order within 1-2 business days
              </NextStepsItem>
              {hasDownloadableProducts && (
                <NextStepsItem>
                  All downloads are also available in your account dashboard
                </NextStepsItem>
              )}
            </NextStepsList>
          </NextSteps>

          <ActionButtons>
            <Button as={Link} to="/dashboard">
              View Order History
            </Button>
            <Button as={Link} to="/shop" variant="outline">
              Continue Shopping
            </Button>
          </ActionButtons>
        </OrderConfirmationCard>
      </Container>
    </OrderConfirmationWrapper>
  );
};

export default OrderConfirmationPage;